import { Printer } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import BadgeCourier from '@/domains/dashboard-management/components/BadgeCourier/BadgeCourier';
import BookInfo from '@/domains/dashboard-management/components/BookInfo/BookInfo';
import ConfirmModal from '@/domains/dashboard-management/components/ConfirmModal/ConfirmModal';
import DetailInfo from '@/domains/dashboard-management/components/DetailInfo/DetailInfo';
import {
  useAdminOrder,
  useCancelAdminOrder,
  useConfirmAdminOrderPayment,
  usePrintAdminOrderLabel,
  useRequestAdminOrderPickup,
} from '@/domains/dashboard-management/hooks/useAdminOrders';
import type { AdminOrderAddress } from '@/domains/dashboard-management/types/order.types';
import {
  orderBadge,
  TRANSFER_STATUS_LABEL,
} from '@/domains/dashboard-management/utils/orderStatus';
import { formatPrice } from '@/domains/mobile-web/utils/price';
import BackAction from '@/shared/components/ui/BackAction/BackAction';
import Breadcrumb from '@/shared/components/ui/Breadcrumb/Breadcrumb';
import Button from '@/shared/components/ui/Button';
import LogList from '@/shared/components/ui/LogList/LogList';
import { formatDateToDDMonYYYY, formatDateToMMDDYYYY, formatTimeOnly } from '@/shared/utils/date';

interface HistoryDetailState {
  code?: string;
}

const rupiah = (value: string | number) => `Rp ${formatPrice(Number(value))}`;

const fullAddress = (address: AdminOrderAddress) =>
  [
    address.address_line,
    address.rt || address.rw ? `RT ${address.rt ?? '-'}/RW ${address.rw ?? '-'}` : null,
    address.subdistrict,
    address.district,
    address.city,
    `${address.province} ${address.postal_code}`,
  ]
    .filter(Boolean)
    .join(', ');

const HistoryDetailManagement = () => {
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('id') ?? undefined;
  const { data: order, isLoading, isError } = useAdminOrder(orderId);
  const cancelOrder = useCancelAdminOrder();
  const requestPickup = useRequestAdminOrderPickup();
  const confirmPayment = useConfirmAdminOrderPayment();
  const printLabel = usePrintAdminOrderLabel();
  const [pending, setPending] = useState<'cancel' | 'pickup' | 'pay' | null>(null);

  const code = order?.order_number ?? (state as HistoryDetailState | null)?.code ?? '';
  const busy = cancelOrder.isPending || requestPickup.isPending || confirmPayment.isPending;

  const confirm = () => {
    if (!order || !pending) return;
    const action = { cancel: cancelOrder, pickup: requestPickup, pay: confirmPayment }[pending];
    action.mutate(order.order_id, { onSettled: () => setPending(null) });
  };

  const header = (
    <div className="inline-flex gap-3 w-full items-center flex-wrap">
      <BackAction />
      <Breadcrumb items={[{ label: 'Riwayat Transaksi' }, { label: code }]} />
    </div>
  );

  if (!order) {
    return (
      <section className="flex! flex-col gap-6 overflow-auto mb-10">
        {header}
        <div className="shadowCard p-6">
          <p className="text-center">
            {!orderId
              ? 'Transaksi tidak ditemukan'
              : isLoading
                ? 'Memuat...'
                : isError
                  ? 'Gagal memuat transaksi'
                  : 'Transaksi tidak ditemukan'}
          </p>
        </div>
      </section>
    );
  }

  const address = order.shipping_address;
  const shipment = order.shipment;
  const payment = order.payment;
  const account = payment?.transfer_account;
  // What reaches the bank account: the total plus the unique code
  const transferAmount = rupiah(payment?.amount ?? order.grand_total);
  const paymentStatus = order.transfer_status
    ? TRANSFER_STATUS_LABEL[order.transfer_status]
    : (payment?.status_label ?? '-');

  return (
    <section className="flex! flex-col gap-6 overflow-auto mb-10">
      {header}

      <div className="shadowCard flex flex-col">
        <div className="w-full p-3 lg:p-6 flex row flex-wrap gap-3 justify-between">
          <div className="shrink-0">
            <p className="title1bold capitalize">Detail Transaksi</p>
            <p className="body1 text-text-grey">{order.order_number}</p>
          </div>
          <BadgeCourier
            statusCourier={orderBadge(order)}
            label={order.transfer_status === 'reported' ? null : order.display_status_label}
          />
        </div>

        <div className=" divider h-[0.5px] w-full bg-blue-cs-200" />

        <section className="w-full p-3 lg:p-6 flex!  flex-wrap gap-6 justify-between">
          <article className="shadowCard p-6 flex flex-col w-full gap-6">
            <div className="shrink-0">
              <p className="title2bold capitalize">Informasi Pembelian</p>
              <p className="body2 text-text-grey">Detail pelanggan dan data utama transaksi</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 lg:grid-cols-4 md:grid-cols-2">
              <DetailInfo title="ID" value={order.order_number} />
              <DetailInfo title="ID INVOICE" value={order.order_number} isCopy />
              <DetailInfo
                title="TANGGAL PEMBELIAN"
                value={formatDateToDDMonYYYY(order.placed_at)}
              />
              <DetailInfo title="JUMLAH BUKU" value={order.book_total} />
              <DetailInfo title="NAMA PEMBELI" value={order.customer.name} />
              <DetailInfo title="NOMOR HANDPHONE" value={order.customer.phone ?? '-'} />
              <DetailInfo title="TOTAL HARGA BUKU" value={rupiah(order.subtotal)} />
              <DetailInfo title="STATUS" value={order.display_status_label ?? '-'} />
            </div>
          </article>
          <article className="shadowCard p-6 flex flex-col w-full gap-6">
            <div className="shrink-0">
              <p className="title2bold capitalize">Rincian Buku</p>
              <p className="body2 text-text-grey">{order.items.length} item dalam pesanan ini</p>
            </div>
            <div className="w-full flex flex-col gap-3">
              <BookInfo
                listBookInfo={order.items.map((item, index) => ({
                  title: `BUKU #${index + 1}`,
                  name: item.title,
                  qty: item.quantity,
                  price: Number(item.line_total),
                }))}
              />
            </div>
          </article>

          <article className="shadowCard p-6 flex flex-col w-full gap-6">
            <div className="w-full items-center flex row flex-wrap gap-3 justify-between">
              <div className="shrink-0">
                <p className="title2bold capitalize">Rincian Pembayaran</p>
                <p className="body2 text-text-grey">Ringkasan pembayaran</p>
              </div>
              <Button
                className="btn-fill-blue "
                disabled={!order.can_confirm_payment || busy}
                onClick={() => setPending('pay')}
              >
                <span>Konfirmasi Pembayaran</span>
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 md:grid-cols-3 gap-6">
              <DetailInfo
                title="METODE PEMBAYARAN"
                value={payment?.method_label ?? payment?.method ?? '-'}
              />
              <DetailInfo
                title="NOMOR REKENING"
                value={account ? `${account.account_number} (${account.bank_name})` : '-'}
              />
              <DetailInfo title="NAMA REKENING" value={account?.account_holder ?? '-'} />
              <DetailInfo title="TOTAL BAYAR" value={transferAmount} />
              <DetailInfo title="STATUS BAYAR" value={paymentStatus} />
              {payment?.unique_code != null && (
                <DetailInfo title="KODE UNIK" value={payment.unique_code} />
              )}
              {payment?.transfer_reported_at && (
                <DetailInfo
                  title="DILAPORKAN PEMBELI"
                  value={`${formatDateToDDMonYYYY(payment.transfer_reported_at)} | ${formatTimeOnly(payment.transfer_reported_at)}`}
                />
              )}
              {order.transfer_status === 'rejected' && payment?.transfer_rejection_reason && (
                <DetailInfo title="ALASAN PENOLAKAN" value={payment.transfer_rejection_reason} />
              )}
            </div>
          </article>

          <article className="shadowCard p-6 flex flex-col w-full gap-6">
            <div className="w-full items-center flex row flex-wrap gap-3 justify-between">
              <div className="shrink-0">
                <p className="title2bold capitalize">Informasi Pengiriman</p>
                <p className="body2 text-text-grey">Kurir, nomor resi, dan alamat penerima</p>
              </div>
              <Button
                className="btn-fill-blue "
                disabled={!order.can_request_pickup || busy}
                onClick={() => setPending('pickup')}
              >
                <span>Request Jemput</span>
              </Button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 lg:grid-cols-6 md:grid-cols-4">
              <DetailInfo
                title="JASA PENGIRIMAN"
                value={shipment?.courier_name ?? shipment?.courier_code?.toUpperCase() ?? '-'}
              />
              <DetailInfo
                title="STATUS PENGIRIMAN"
                value={shipment?.status_label ?? order.pickup_status_label ?? '-'}
              />
              <DetailInfo title="PROVINSI" value={address.province} />
              <DetailInfo title="KECAMATAN" value={address.district} />
              <DetailInfo title="RT" value={address.rt ?? '-'} />
              <DetailInfo title="KODE POS" value={address.postal_code} />
              <DetailInfo
                title="NOMOR RESI"
                value={shipment?.tracking_number ?? '-'}
                isCopy={Boolean(shipment?.tracking_number)}
              >
                <Button
                  className="btn-fill-blue"
                  disabled={!order.can_print_label || printLabel.isPending}
                  onClick={() => printLabel.print(order.order_id)}
                >
                  <Printer />
                  <span>Cetak Resi</span>
                </Button>
              </DetailInfo>
              <DetailInfo title="LABEL ALAMAT" value={address.label ?? '-'} />
              <DetailInfo title="KOTA/KABUPATEN" value={address.city} />
              <DetailInfo title="KELURAHAN/DESA" value={address.subdistrict} />
              <DetailInfo title="RW" value={address.rw ?? '-'} />
            </div>
            <div className="bg-[#0069A80A] p-3 rounded-[12px]">
              <DetailInfo title="ALAMAT LENGKAP" value={fullAddress(address)} />
            </div>
          </article>
        </section>

        <div className=" divider h-[0.5px] w-full bg-blue-cs-200" />

        <div className="p-6 w-full inline-flex  gap-3 justify-between items-center flex-wrap">
          <LogList
            logs={[
              { label: 'Pendaftar', value: order.customer.name },
              {
                label: 'Waktu Daftar',
                value: `${formatDateToMMDDYYYY(order.placed_at).replace(/\//g, '-')} | ${formatTimeOnly(order.placed_at)}`,
              },
            ]}
          />

          <Button
            className="btn-fill-red flex-1 lg:flex-none md:flex-none basis-[600px]"
            disabled={!order.can_cancel || busy}
            onClick={() => setPending('cancel')}
          >
            <span>Batalkan</span>
          </Button>
        </div>
      </div>

      <ConfirmModal
        open={pending === 'cancel'}
        title="Batalkan Transaksi ?"
        message="Apakah Anda yakin ingin membatalkan transaksi ini?"
        confirmLabel="Ya, Batalkan"
        variant="danger"
        loading={busy}
        onConfirm={confirm}
        onClose={() => setPending(null)}
      />
      <ConfirmModal
        open={pending === 'pay'}
        title="Konfirmasi Pembayaran ?"
        message={`Pastikan transfer ${transferAmount} sudah masuk ke rekening. Pesanan diproses setelah dikonfirmasi.`}
        confirmLabel="Ya, Konfirmasi"
        variant="success"
        loading={busy}
        onConfirm={confirm}
        onClose={() => setPending(null)}
      />
      <ConfirmModal
        open={pending === 'pickup'}
        title="Konfirmasi Penjemputan ?"
        message="Apakah Anda yakin ingin memproses penjemputan ke lokasi pengirim?"
        confirmLabel="Ya, Jemput"
        variant="primary"
        loading={busy}
        onConfirm={confirm}
        onClose={() => setPending(null)}
      />
    </section>
  );
};

export default HistoryDetailManagement;
